import { createSlice } from "@reduxjs/toolkit";
import _find from 'lodash/find';
import _map from 'lodash/map';
import _filter from 'lodash/filter';
import _omit from 'lodash/omit';
import _uniqBy from 'lodash/uniqBy';
import {
  getMessages,
  getMessage,
  createMessage,
  deleteMessage,
  approveMessage,
  rejectMessage,
  createLocalMessage, 
  deleteLocalMessage, 
  setLocalMessageError, 
  setLocalMessageFields, 
  clearLocalMessages, 
  clearLocalActiveMessage
} from './actions';

export const initialState = {
  data: [],
  upPagination: { limit: 25, offset: 0, count: 0, total_count: 0 },
  downPagination: { limit: 25, offset: 0, count: 0, total_count: 0 },
  message: { id: '', sender: '', channel: '', status: '', text: '', created_at: '' },
  loading: {all: false, message: false}
}

const  slice = createSlice({
  name: 'messages',
  initialState,
  extraReducers(builder) {
    builder
      .addCase(getMessages.pending, (state) => {
        state.loading.all = true;
      })
      .addCase(getMessages.fulfilled, (state, action) => {
        const { data: { messages, pagination }, direction } = action.payload;
        
        state.loading.all = false;
        if (!direction) {
          state.data = _uniqBy([...state.data, ...messages], 'id');
          state.upPagination = pagination;
          state.downPagination = pagination;
        } 
        if (direction === 'up') {
          state.data = _uniqBy([...state.data, ...messages], 'id');
          state.upPagination = pagination;
        }
        if (direction === 'down') {
          state.data = _uniqBy([...messages, ...state.data], 'id');
          state.downPagination = pagination;
        } 
      })
      .addCase(getMessages.rejected, (state) => {
        state.loading.all = false;
      })
      .addCase(getMessage.pending, (state) => {
        state.loading.message = true;
      })
      .addCase(getMessage.fulfilled, (state, action) => {
        const { data: { message }, cable } = action.payload;
        
        state.loading.message = false;
        state.message = { ...state.message, ...message };
        if (!_find(state.data, (item) => item.id === message.id)) {
          state.data = [...[{ ...message, cable }], ...state.data];
          state.upPagination.count = state.upPagination.count + (state.upPagination.count < state.upPagination.limit ? 1 : 0);
          state.upPagination.total_count = state.upPagination.total_count + 1;
          state.downPagination.total_count = state.downPagination.total_count + 1;
        }
      })
      .addCase(getMessage.rejected, (state) => {
        state.loading.message = false;
      })
      .addCase(createMessage.pending, (state) => {
        state.loading.message = true;
      })
      .addCase(createMessage.fulfilled, (state, action) => {
        const { data: { message }, local_id } = action.payload;
        
        state.loading.message = false;
        if (!!_find(state.data, (item) => item.id === local_id)) {
          state.data = _map(state.data.slice(0, !!state.upPagination.offset ? state.data.length : state.upPagination.limit), (item) => item.id === local_id ? message : item)
        }
      })
      .addCase(createMessage.rejected, (state) => {
        state.loading.message = false;
      })
      .addCase(deleteMessage.pending, (state) => {
        state.loading.message = true;
      })
      .addCase(deleteMessage.fulfilled, (state, action) => {
        const { status, id } = action.payload;
        
        state.loading.message = false;
        if (status === 204) {
          state.data =  _filter(state.data, (item) => item.id !== id);
          state.downPagination.total_count = state.downPagination.total_count - 1;
        }
      })
      .addCase(deleteMessage.rejected, (state) => {
        state.loading.message = false;
      })
      .addCase(approveMessage.pending, (state) => {
        state.loading.message = true;
      })
      .addCase(approveMessage.fulfilled, (state, action) => {
        const { status, id } = action.payload;
        
        state.loading.message = false;
        if (status === 204) {
          state.data = _map(state.data, (item) => item.id === id ? { ...item, status: 'approved' } : item);
        }
      })
      .addCase(approveMessage.rejected, (state) => {
        state.loading.message = false;
      })
      .addCase(rejectMessage.pending, (state) => {
        state.loading.message = true;
      })
      .addCase(rejectMessage.fulfilled, (state, action) => {
        const { status, id } = action.payload;
        
        state.loading.message = false;
        if (status === 204) {
          state.data = _map(state.data, (item) => item.id === id ? { ...item, status: 'rejected' } : item);
        }
      })
      .addCase(rejectMessage.rejected, (state) => {
        state.loading.message = false;
      })
      .addCase(createLocalMessage, (state, action) => {
        const message = action.payload;

        state.data = [...[message], ...state.data];
        state.upPagination.count = state.upPagination.count + (state.upPagination.count < state.upPagination.limit ? 1 : 0);
        state.upPagination.total_count = state.upPagination.total_count + 1;
        state.downPagination.total_count = state.downPagination.total_count + 1;
      })
      .addCase(deleteLocalMessage, (state, action) => {
        const id = action.payload;

        if (!!_find(state.data, (item) => item.id === id)) {
          state.data = _filter(state.data, (item) => item.id !== action.payload);
          state.upPagination.count = state.upPagination.count - 1;
          state.upPagination.total_count = state.upPagination.total_count - 1;
        }
      })
      .addCase(setLocalMessageError, (state, action) => {
        const id = action.payload;
        
        state.data = _map(state.data, message => message.id === id ? { ...message, hasError: true } : message);
      })
      .addCase(setLocalMessageFields, (state, action) => {
        const id = action.payload;
        
        if (!!_find(state.data, (item) => item.id === id)) {
          state.data = _map(state.data, (item) => item.id === id ? { ..._omit(item, ['cable']) } : item);
        }
      })
      .addCase(clearLocalMessages, state => {
        state.data = initialState.data;
        state.upPagination = initialState.upPagination;
        state.downPagination = initialState.downPagination;
      })
      .addCase(clearLocalActiveMessage, state => {
        state.message = initialState.message;
      })
  }
})

export default slice.reducer;